import { useMutation, UseMutationOptions, useQuery, useQueryClient } from "@tanstack/react-query"

import {
  getPaymentPolicyAgreement,
  savePaymentPolicyAgreement,
  SavePaymentPolicyPayload
} from "@/api"

const PAYMENT_POLICY_QUERY_KEY = ["payment_policy_agreement"]

/**
 * Hook to check if user has agreed to payment policy
 * Returns status: false if API errors or returns null (no data yet)
 */
export const useGetPaymentPolicyAgreement = () => {
  return useQuery({
    queryKey: PAYMENT_POLICY_QUERY_KEY,
    queryFn: async () => {
      try {
        const response = await getPaymentPolicyAgreement()
        // If API returns null data, treat as not agreed
        if (!response.data?.data) {
          return { data: { status: false, type: null } }
        }
        return response.data
      } catch {
        return { data: { status: false, type: null } }
      }
    }
  })
}

/**
 * Hook to save user's payment policy agreement
 */
export const useSavePaymentPolicyAgreement = (
  options: UseMutationOptions<unknown, Error, SavePaymentPolicyPayload> = {}
) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: SavePaymentPolicyPayload) => {
      const response = await savePaymentPolicyAgreement(data)
      return response.data
    },
    onSuccess: (...args) => {
      // Invalidate the policy query to refetch the status
      queryClient.invalidateQueries({ queryKey: PAYMENT_POLICY_QUERY_KEY })
      options.onSuccess?.(...args)
    },
    ...options
  })
}

/**
 * Get detailed device name from user agent
 * Returns format: "Device - Browser on OS"
 */
export const getDeviceName = (): string => {
  const userAgent = navigator.userAgent

  // Detect device
  let device = "Unknown Device"
  if (/iPhone/.test(userAgent)) {
    device = "iPhone"
  } else if (/iPad/.test(userAgent)) {
    device = "iPad"
  } else if (/Android/.test(userAgent)) {
    const match = userAgent.match(/Android.*?;\s*([^)]+)\)/)
    device = match ? match[1].split(" Build")[0].trim() : "Android Device"
  } else if (/Macintosh/.test(userAgent)) {
    device = "Mac"
  } else if (/Windows/.test(userAgent)) {
    device = "Windows PC"
  } else if (/Linux/.test(userAgent)) {
    device = "Linux PC"
  }

  // Detect browser
  let browser = "Unknown Browser"
  if (/Edg\//.test(userAgent)) {
    const match = userAgent.match(/Edg\/([\d.]+)/)
    browser = `Edge ${match?.[1] || ""}`
  } else if (/Chrome\//.test(userAgent) && !/Edg\//.test(userAgent)) {
    const match = userAgent.match(/Chrome\/([\d.]+)/)
    browser = `Chrome ${match?.[1] || ""}`
  } else if (/Safari\//.test(userAgent) && !/Chrome\//.test(userAgent)) {
    const match = userAgent.match(/Version\/([\d.]+)/)
    browser = `Safari ${match?.[1] || ""}`
  } else if (/Firefox\//.test(userAgent)) {
    const match = userAgent.match(/Firefox\/([\d.]+)/)
    browser = `Firefox ${match?.[1] || ""}`
  }

  // Detect OS version
  let os = ""
  if (/iPhone OS ([\d_]+)/.test(userAgent)) {
    const match = userAgent.match(/iPhone OS ([\d_]+)/)
    os = `iOS ${match?.[1]?.replace(/_/g, ".") || ""}`
  } else if (/Android ([\d.]+)/.test(userAgent)) {
    const match = userAgent.match(/Android ([\d.]+)/)
    os = `Android ${match?.[1] || ""}`
  } else if (/Mac OS X ([\d_]+)/.test(userAgent)) {
    const match = userAgent.match(/Mac OS X ([\d_]+)/)
    os = `macOS ${match?.[1]?.replace(/_/g, ".") || ""}`
  } else if (/Windows NT ([\d.]+)/.test(userAgent)) {
    const match = userAgent.match(/Windows NT ([\d.]+)/)
    const ntVersion = match?.[1]
    // Map Windows NT version to name
    const winVersionMap: { [key: string]: string } = {
      "10.0": "10/11",
      "6.3": "8.1",
      "6.2": "8",
      "6.1": "7"
    }
    os = `Windows ${winVersionMap[ntVersion || ""] || ntVersion || ""}`
  }

  // Combine: "Device - Browser on OS"
  const parts = [device]
  if (browser !== "Unknown Browser") {
    parts.push(browser)
  }
  if (os) {
    parts.push(`on ${os}`)
  }

  return parts.join(" - ")
}
